import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';

import FormInputText from '../../components/form/FormInputText';
import FormPassword from '../../components/form/FormPassword';
import { IntegrationSettings } from '../../models/integrationSettings';
import { IntegrationStatus, IntegrationVendor } from '../../models/enums/integrationEnums';
import { parseIntegrationData } from '../../utils/misc-utils';
import { urlField, secretField, credentialField } from '../../utils/validation-helper';

type VestaLosFormValues = {
  baseUrl: string;
  apiKey: string;
  pipelineNmlsColumnId?: string;
  pipelineViewId?: string;
  pipelineSortColumnId?: string;
  pipelineUserId?: string;
};

interface EditVestaLosIntegrationSettingsProps {
  onCancel: () => void;
  onSave?: (_settings: IntegrationSettings) => void;
  integrationSettings?: IntegrationSettings;
  environment: string;
}

export const EditVestaLosIntegrationSettings = ({
  onCancel,
  onSave,
  integrationSettings,
  environment,
}: EditVestaLosIntegrationSettingsProps): JSX.Element => {
  // Determine if we're in edit mode
  const isEditMode = !!integrationSettings;

  let baseUrl = '';
  let apiKey = '';
  let pipelineNmlsColumnId = '';
  let pipelineViewId = '';
  let pipelineSortColumnId = '';
  let pipelineUserId = '';
  if (isEditMode && integrationSettings?.integrationData) {
    const data = parseIntegrationData(integrationSettings.integrationData);
    baseUrl = data.baseUrl || '';
    apiKey = data.apiKey || '';
    pipelineNmlsColumnId = data.pipelineNmlsColumnId || '';
    pipelineViewId = data.pipelineViewId || '';
    pipelineSortColumnId = data.pipelineSortColumnId || '';
    pipelineUserId = data.pipelineUserId || '';
  }

  const validationSchema = yup.object().shape({
    baseUrl: urlField({ label: 'Base URL' }).required('Base URL is required'),
    apiKey: secretField('API Key').required('API Key is required'),
    pipelineNmlsColumnId: credentialField('Pipeline NMLS Column ID'),
    pipelineViewId: credentialField('Pipeline View ID'),
    pipelineSortColumnId: credentialField('Pipeline Sort Column ID'),
    pipelineUserId: credentialField('Pipeline User ID'),
  });

  const { control, handleSubmit, formState } = useForm<VestaLosFormValues>({
    defaultValues: {
      baseUrl: baseUrl,
      apiKey: apiKey,
      pipelineNmlsColumnId: pipelineNmlsColumnId,
      pipelineViewId: pipelineViewId,
      pipelineSortColumnId: pipelineSortColumnId,
      pipelineUserId: pipelineUserId,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
  });

  const { errors } = formState;

  return (
    <div className="surface-section p-4 border-1 surface-border border-round mb-3">
      <h3 className="mt-0 mb-3">{isEditMode ? 'Edit Vesta LOS Settings' : 'Vesta LOS Settings'}</h3>
      <form
        autoComplete="off"
        onSubmit={handleSubmit((data) => {

          // Create or update IntegrationSettings object when form is valid
          if (onSave) {
            let settings: IntegrationSettings;

            if (isEditMode && integrationSettings) {
              // Update existing settings
              settings = {
                ...integrationSettings,
                vendor: IntegrationVendor.Vesta,
                environment: environment,
                integrationData: JSON.stringify({
                  baseUrl: data.baseUrl,
                  apiKey: data.apiKey,
                  pipelineNmlsColumnId: data.pipelineNmlsColumnId,
                  pipelineViewId: data.pipelineViewId,
                  pipelineSortColumnId: data.pipelineSortColumnId,
                  pipelineUserId: data.pipelineUserId,
                }),
              };
            } else {
              // Create new settings
              settings = {
                name: 'Vesta LOS Integration', // This will be overridden by parent
                vendor: IntegrationVendor.Vesta,
                status: IntegrationStatus.Active,
                environment: environment,
                integrationData: JSON.stringify({
                  baseUrl: data.baseUrl,
                  apiKey: data.apiKey,
                  pipelineNmlsColumnId: data.pipelineNmlsColumnId,
                  pipelineViewId: data.pipelineViewId,
                  pipelineSortColumnId: data.pipelineSortColumnId,
                  pipelineUserId: data.pipelineUserId,
                }),
              };
            }

            onSave(settings);
          }
        })}
        noValidate
        className="pb-3 p-fluid p-input-filled"
      >
        <div className="field mb-4">
          <FormInputText control={control} errors={errors} field="baseUrl" label="Base URL *" placeholder="Enter Base URL" />
        </div>

        <div className="field mb-4">
          <FormPassword
            control={control}
            errors={errors}
            field="apiKey"
            label="API Key *"
            placeholder="Enter API Key"
            feedback={false}
            toggleMask
          />
        </div>

        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="pipelineNmlsColumnId"
            label="Pipeline NMLS Column ID"
            placeholder="Enter Pipeline NMLS Column ID (GUID)"
          />
        </div>

        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="pipelineViewId"
            label="Pipeline View ID"
            placeholder="Enter Pipeline View ID (GUID)"
          />
        </div>

        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="pipelineSortColumnId"
            label="Pipeline Sort Column ID"
            placeholder="Enter Pipeline Sort Column ID (GUID)"
          />
        </div>

        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="pipelineUserId"
            label="Pipeline User ID"
            placeholder="Enter Pipeline User ID (GUID)"
          />
        </div>

        <div className="flex justify-content-end gap-2 mt-4" style={{ zIndex: 1000 }}>
          <Button
            type="button"
            label="Cancel"
            className="p-button p-button-outlined"
            style={{ width: 'auto', minWidth: 'auto' }}
            onClick={onCancel}
          />
          <Button
            type="submit"
            label={isEditMode ? 'Update' : 'Save'}
            className="p-button"
            style={{ width: 'auto', minWidth: 'auto' }}
          />
        </div>
      </form>
    </div>
  );
};
